import { CampusRef } from "../../../../services/schools/campus.types";
import { ClassEntity, SchoolUserEntity } from "../types";

/**
 * SCRUM-916: sentinel of the "Todos los planteles" option, cloned from the
 * `ALL_SCHOOLS` idiom of Formacion/index.tsx. It is a UI-only value: the
 * backend reads the ABSENCE of `campusId` as "the whole tree", so the sentinel
 * is dropped from the request instead of being sent.
 */
export const ALL_CAMPUSES = 0;
export const ALL_CAMPUSES_LABEL = "Todos los planteles";

/**
 * A school without children is the majority case (147 of 148): every campus
 * affordance stays hidden there, so the tab looks exactly like before.
 */
export const hasMultipleCampuses = (campuses: CampusRef[]): boolean =>
  campuses.length > 1;

export const campusOptions = (
  campuses: CampusRef[]
): { value: number; label: string }[] => [
  { value: ALL_CAMPUSES, label: ALL_CAMPUSES_LABEL },
  ...campuses.map((campus) => ({ value: campus.id, label: campus.name })),
];

export interface UserFilterInput {
  role: string;
  isActive: string;
  gradeId?: number;
  classroomId?: number;
  search: string;
  campusId?: number;
}

/**
 * Builds the query filters of `GET /schools/:schoolId/users`. Every filter has
 * a neutral value that must not reach the wire — "all" for the selects, an
 * empty search, and `ALL_CAMPUSES` for the campus picker.
 */
export const buildUserFilters = (
  input: UserFilterInput
): Record<string, unknown> => {
  const filters: Record<string, unknown> = {};
  if (input.role !== "all") filters.role = input.role;
  if (input.gradeId) filters.grade = input.gradeId;
  if (input.search) filters.search = input.search;
  if (input.classroomId) filters.classroom_id = input.classroomId;
  if (input.isActive !== "all") filters.is_active = input.isActive;
  if (input.campusId && input.campusId !== ALL_CAMPUSES) {
    filters.campusId = input.campusId;
  }
  return filters;
};

/**
 * classroom-group-model: the school classrooms API returns one virtual row
 * per licensed program (same classroom id repeated N times). Any UI that
 * lists CLASSES — filter dropdowns, class pickers — must collapse them back
 * to one entry per physical classroom or every group shows N times.
 */
export const uniqueClassrooms = <T extends Pick<ClassEntity, "id">>(
  classes: T[]
): T[] => {
  const seen = new Set<number>();
  return classes.filter((classItem) => {
    if (seen.has(classItem.id)) return false;
    seen.add(classItem.id);
    return true;
  });
};

/**
 * SCRUM-757: the welcome email carries a complete-registration link, and only
 * TEACHER/COORDINATOR accounts have that flow — the backend bulk endpoint
 * filters by those roles too, so a student row must not offer the action.
 */
export const canResendWelcomeEmail = (
  user: Pick<SchoolUserEntity, "role" | "email">
): boolean =>
  (user.role === "TEACHER" || user.role === "COORDINATOR") &&
  Boolean(user.email && user.email.trim());

/**
 * The bulk resend endpoint answers 200 even when it delivered nothing (no
 * matching teacher, missing email, mailer failure), so only a non-zero `sent`
 * proves the email left.
 */
export const didResendWelcomeSucceed = (
  result: { sent: number; failed?: number } | null
): boolean => Boolean(result && result.sent > 0);

/** What the backend answers (409) when another active user holds the email. */
const EMAIL_TAKEN_MESSAGE = "Email is already in use";

/**
 * The toast text when saving a user fails. The backend's own message says why
 * (grades, validation), except the duplicated email, which only the creation
 * flow catches beforehand and the backend sends in English.
 */
export const saveUserErrorMessage = (error: any): string => {
  const backendMessage = error?.response?.data?.message;
  if (backendMessage === EMAIL_TAKEN_MESSAGE) {
    return "Ese correo ya lo usa otro usuario.";
  }
  return (
    backendMessage ||
    error?.message ||
    "Ocurrió un error al guardar el usuario."
  );
};
